// Lens scales and legends: value → 0..1 for the shader, and the legend stops that label it.
//
// Colours here are display defaults for the lens LUT legend, not data.

export type ScaleKind = 'linear' | 'log';

export interface Scale {
  kind: ScaleKind;
  lo: number;
  hi: number;
}

/** Sequential ramp, low → high (5 stops, matching LEGEND_STOPS). */
export const RAMP = ['#2b1d4e', '#45438f', '#2f8a8f', '#79c267', '#f4e36b'] as const;

const LEGEND_STOPS = RAMP.length;

const compact = new Intl.NumberFormat('en', { notation: 'compact', maximumSignificantDigits: 3 });

export function fmtNum(v: number): string {
  if (Math.abs(v) < 1 && v !== 0) {
    return v.toPrecision(2);
  }
  return compact.format(v);
}

/** Value → 0..1, clamped. NaN in → NaN out. */
export function toUnit(s: Scale, v: number): number {
  if (Number.isNaN(v)) {
    return NaN;
  }
  if (s.hi <= s.lo) {
    return 0;
  }
  const t = s.kind === 'log'
    ? (Math.log(Math.max(v, s.lo)) - Math.log(s.lo)) / (Math.log(s.hi) - Math.log(s.lo))
    : (v - s.lo) / (s.hi - s.lo);
  return Math.min(1, Math.max(0, t));
}

/** 0..1 → value (for legend labels). */
export function fromUnit(s: Scale, t: number): number {
  if (s.kind === 'log') {
    return Math.exp(Math.log(s.lo) + t * (Math.log(s.hi) - Math.log(s.lo)));
  }
  return s.lo + t * (s.hi - s.lo);
}

/** Evenly spaced legend stops, labelled with the scale's values. */
export function rampLegend(s: Scale, fmt: (v: number) => string): Array<{ label: string; color: string }> {
  return RAMP.map((color, i) => ({ label: fmt(fromUnit(s, i / (LEGEND_STOPS - 1))), color }));
}

/** Legend stops with fixed labels (e.g. staleness in days). */
export function labelledRamp(labels: readonly string[]): Array<{ label: string; color: string }> {
  return RAMP.map((color, i) => ({ label: labels[i] ?? '', color }));
}
